/**
 * API utility functions for organization-scoped requests
 */

/**
 * Strip markdown formatting from text for clean display
 * @param {string} text - Text potentially containing markdown
 * @returns {string} Text with markdown formatting removed
 */
export const stripMarkdown = (text) => {
  if (!text) return text;
  // Remove bold markers **text** or __text__
  let result = text.replace(/\*\*(.+?)\*\*/g, '$1');
  result = result.replace(/__(.+?)__/g, '$1');
  // Remove italic markers *text* or _text_
  result = result.replace(/\*([^*]+)\*/g, '$1');
  result = result.replace(/_([^_]+)_/g, '$1');
  // Remove inline code `text`
  result = result.replace(/`([^`]+)`/g, '$1');
  // Remove heading markers
  result = result.replace(/^#+\s*/gm, '');
  return result;
};

/**
 * Get headers for organization-scoped API requests
 * @param {string} organizationId - The active organization ID
 * @param {boolean} isFormData - Whether the request body is FormData (skip Content-Type)
 * @returns {Object} Headers object
 */
export const getOrgHeaders = (organizationId, isFormData = false) => {
  const headers = {};
  
  // Don't set Content-Type for FormData - browser will set it with boundary
  if (!isFormData) {
    headers['Content-Type'] = 'application/json';
  }
  
  if (organizationId) {
    headers['X-Organization-Id'] = organizationId;
  }
  
  return headers;
};

/**
 * Make a fetch request with organization context
 * @param {string} url - The API endpoint URL
 * @param {Object} options - Fetch options
 * @param {string} organizationId - The active organization ID
 * @returns {Promise} Fetch promise
 */
export const fetchWithOrg = (url, options = {}, organizationId = null) => {
  // Detect if body is FormData to avoid setting Content-Type
  const isFormData = options.body instanceof FormData;
  
  const headers = {
    ...getOrgHeaders(organizationId, isFormData),
    ...(options.headers || {})
  };
  
  // Remove Content-Type if FormData (in case it was passed in options.headers)
  if (isFormData && headers['Content-Type']) {
    delete headers['Content-Type'];
  }
  
  return fetch(url, {
    ...options,
    credentials: 'include',
    headers
  });
};
